import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  TrendingUp,
  Users,
  Repeat,
  Zap,
  Target,
  Sparkles,
  Trophy,
  CheckCircle2,
  Circle,
  Loader2,
  DollarSign,
  RefreshCw,
  FileText,
  BookOpen,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Lifted out of the component so eslint's react-hooks rule never inspects it
// for setState calls. The component just orchestrates state transitions.
async function callMarkMilestonePaidAPI(token, threshold) {
  const res = await fetch(`${BACKEND_URL}/api/admin/analytics/vp-kpi-dashboard/mark-milestone-paid`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ subs_threshold: threshold, payment_method: "PayPal", note: "" }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

// Map KPI id → icon, color tint, and a copy-paste-ready formula label
const KPI_META = {
  1: { icon: TrendingUp, color: "emerald", formulaShort: "Q paid signups" },
  2: { icon: Target, color: "blue", formulaShort: "Trial → paid %" },
  3: { icon: Repeat, color: "purple", formulaShort: "Referrals per signup" },
  4: { icon: Users, color: "amber", formulaShort: "Sales subs / rep / mo (90d)" },
  5: { icon: Sparkles, color: "rose", formulaShort: "40 posts/mo + reach OR follows" },
};

const TONE = {
  emerald: { ring: "ring-emerald-200", bar: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: "text-emerald-600" },
  blue: { ring: "ring-blue-200", bar: "bg-blue-500", chip: "bg-blue-50 text-blue-700 border-blue-200", icon: "text-blue-600" },
  purple: { ring: "ring-purple-200", bar: "bg-purple-500", chip: "bg-purple-50 text-purple-700 border-purple-200", icon: "text-purple-600" },
  amber: { ring: "ring-amber-200", bar: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200", icon: "text-amber-600" },
  rose: { ring: "ring-rose-200", bar: "bg-rose-500", chip: "bg-rose-50 text-rose-700 border-rose-200", icon: "text-rose-600" },
};

const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString() : n);
const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AdminVPKpiTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(null);
  const token = localStorage.getItem("auth_token");

  const authFetch = useCallback(
    (path, opts = {}) =>
      fetch(`${BACKEND_URL}${path}`, {
        ...opts,
        headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
      }),
    [token]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch("/api/admin/analytics/vp-kpi-dashboard");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const onMarkMilestonePaid = (threshold) => {
    setMarkingPaid(threshold);
    callMarkMilestonePaidAPI(token, threshold)
      .catch((e) => alert(String(e?.message || e)))
      .finally(() => {
        setMarkingPaid(null);
        fetchData();
      });
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="vp-kpi-loading">
        <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-rose-700 text-sm" data-testid="vp-kpi-error">
          Failed to load VP KPI dashboard: {error}
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const { quarter, kpis, summary, subscriber_milestones, next_unpaid_milestone, total_paid_subs_lifetime } = data;

  return (
    <div className="space-y-4" data-testid="vp-kpi-tab">
      {/* Header */}
      <Card className="border-gray-200">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" /> VP Marketing — Q{quarter.index} KPI Scoreboard
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {new Date(quarter.start).toLocaleDateString()} – {new Date(quarter.end).toLocaleDateString()} · {quarter.days_remaining} days remaining
            </p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm" disabled={loading} data-testid="vp-kpi-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </CardContent>
      </Card>

      {/* Document downloads — for handoff to candidate VPs and legal review */}
      <Card className="border-gray-200 bg-gradient-to-br from-slate-50 to-white">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 mb-3">
            <FileText className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-sm text-gray-900">Hiring Packet — Downloadable Docs</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Share with VP candidates before/during the offer stage. The compensation agreement is the binding contract; the plain-language summary helps the candidate understand the KPIs without legalese.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <a
              href={`${BACKEND_URL}/api/admin/analytics/vp-kpi-dashboard/download/compensation-agreement`}
              download
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors text-sm group"
              data-testid="download-comp-agreement"
            >
              <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">Compensation Agreement</div>
                <div className="text-[11px] text-gray-500">Legal-ready · for attorney review</div>
              </div>
              <DollarSign className="h-3.5 w-3.5 text-gray-400 group-hover:text-blue-500" />
            </a>
            <a
              href={`${BACKEND_URL}/api/admin/analytics/vp-kpi-dashboard/download/kpi-summary-plain`}
              download
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors text-sm group"
              data-testid="download-kpi-summary"
            >
              <BookOpen className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">5 KPIs — Plain English</div>
                <div className="text-[11px] text-gray-500">Easy-read guide for the VP</div>
              </div>
              <Sparkles className="h-3.5 w-3.5 text-gray-400 group-hover:text-emerald-500" />
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-3">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">KPIs Met</div>
          <div className="text-2xl font-bold text-gray-900" data-testid="vp-kpi-met">{summary.kpis_met}/{summary.kpis_total}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">Profit Share Earned</div>
          <div className="text-2xl font-bold text-emerald-600" data-testid="vp-kpi-profit-pct">{summary.profit_share_pct_earned}%</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">Est. Q Net Profit</div>
          <div className="text-2xl font-bold text-gray-900">{fmtUSD(summary.est_quarter_net_profit_usd)}</div>
          <div className="text-[10px] text-gray-400 mt-0.5">
            Rev {fmtUSD(summary.est_quarter_revenue_usd)} − VP {fmtUSD(summary.est_quarter_vp_commission_usd)} − Sales {fmtUSD(summary.est_quarter_sales_commission_usd)}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">Est. Total Bonus</div>
          <div className="text-2xl font-bold text-amber-600" data-testid="vp-kpi-total-bonus">{fmtUSD(summary.est_total_bonus_usd)}</div>
          <div className="text-[10px] text-gray-400 mt-0.5">{fmtUSD(summary.est_bonus_per_kpi_usd)} per KPI</div>
        </CardContent></Card>
      </div>

      {/* Subscriber mix + commission rates breakdown */}
      <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">MRR Subscribers</div>
              <div className="text-lg font-bold text-gray-900" data-testid="vp-kpi-mrr-subs">
                {data.subscriber_breakdown.mrr_subs}
              </div>
              <div className="text-[11px] text-gray-500">@ $4.99/mo</div>
            </div>
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">ARR Subscribers</div>
              <div className="text-lg font-bold text-gray-900" data-testid="vp-kpi-arr-subs">
                {data.subscriber_breakdown.arr_subs}
              </div>
              <div className="text-[11px] text-gray-500">@ $45/yr ({data.subscriber_breakdown.arr_mix_pct}% of all subs)</div>
            </div>
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">VP Commission Rate</div>
              <div className="text-lg font-bold text-blue-700">
                $2 <span className="text-xs font-normal text-gray-500">MRR</span> · $15 <span className="text-xs font-normal text-gray-500">ARR</span>
              </div>
              <div className="text-[11px] text-gray-500">Capped at $10K/mo</div>
            </div>
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">Sales Commission Rate</div>
              <div className="text-lg font-bold text-blue-700">
                $1.75 <span className="text-xs font-normal text-gray-500">MRR</span> · $15 <span className="text-xs font-normal text-gray-500">ARR</span>
              </div>
              <div className="text-[11px] text-gray-500">per sub</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="space-y-2">
        {kpis.map((k) => {
          const meta = KPI_META[k.id] || KPI_META[1];
          const tone = TONE[meta.color];
          const Icon = meta.icon;
          const pctClamped = Math.min(k.progress_pct, 100);
          // KPI 5 has nested current/target — format separately
          let currentLabel = "";
          let targetLabel = "";
          if (k.id === 5) {
            currentLabel = `${k.current.pieces_per_month} posts/mo · ${fmtNum(k.current.impressions)} impr · ${fmtNum(k.current.follows)} follows`;
            targetLabel = `${k.target.pieces_per_month} posts/mo + ${k.target.impressions_or_follows}`;
          } else if (k.id === 2) {
            currentLabel = `${k.current}%`;
            targetLabel = `≥${k.target}%`;
          } else {
            currentLabel = String(k.current);
            targetLabel = `≥${k.target}`;
          }

          return (
            <Card key={k.id} className={`border ${k.met ? `${tone.ring} ring-2` : "border-gray-200"}`} data-testid={`vp-kpi-card-${k.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg bg-gray-50`}>
                    <Icon className={`h-4 w-4 ${tone.icon}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <h3 className="font-semibold text-sm text-gray-900">
                        KPI {k.id}: {k.name}
                      </h3>
                      <Badge className={`${tone.chip} border text-[10px] font-bold whitespace-nowrap`} data-testid={`vp-kpi-status-${k.id}`}>
                        {k.met ? <><CheckCircle2 className="h-3 w-3 mr-0.5 inline" />MET · +1%</> : <><Circle className="h-3 w-3 mr-0.5 inline" />0%</>}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{k.description}</p>
                    <div className="flex items-center gap-3 text-xs">
                      <div className="flex-1">
                        <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                          <div className={`h-1.5 ${tone.bar} transition-all`} style={{ width: `${pctClamped}%` }} />
                        </div>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <span className="font-bold text-gray-900">{currentLabel}</span>
                        <span className="text-gray-400"> / {targetLabel}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Subscriber Milestones */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-500" />
            Subscriber Milestone Bridge Bonuses
            <Badge variant="outline" className="text-[10px] ml-1">Lifetime: {total_paid_subs_lifetime} paid subs</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-2">
          {/* Bridge phase status indicator */}
          {data.bridge_phase && (
            <div
              className={`rounded-lg p-3 border ${
                data.bridge_phase.active
                  ? "bg-emerald-50 border-emerald-200"
                  : "bg-gray-100 border-gray-300"
              }`}
              data-testid="vp-kpi-bridge-phase-banner"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className={`text-xs font-bold ${data.bridge_phase.active ? "text-emerald-800" : "text-gray-600"}`}>
                    {data.bridge_phase.active ? "BRIDGE PHASE ACTIVE" : "BRIDGE PHASE ENDED — KPI profit-share only"}
                  </div>
                  <div className="text-[11px] text-gray-600 mt-0.5 leading-snug">
                    Current MRR: <strong>{fmtUSD(data.current_mrr)}</strong>
                    {data.bridge_phase.active ? (
                      <> · <strong>{fmtUSD(data.bridge_phase.mrr_to_cutoff)}</strong> until cutoff at {fmtUSD(data.bridge_phase.mrr_cutoff)}</>
                    ) : (
                      <> · Past {fmtUSD(data.bridge_phase.mrr_cutoff)} cutoff. Quarterly KPI profit-share (up to 5%) is now the sole variable comp.</>
                    )}
                  </div>
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className="text-xs font-bold text-gray-900">{data.bridge_phase.pct_to_cutoff}%</div>
                  <div className="text-[10px] text-gray-500">to cutoff</div>
                </div>
              </div>
              <div className="w-full bg-white/70 rounded-full h-1.5 mt-2 overflow-hidden">
                <div
                  className={`h-1.5 ${data.bridge_phase.active ? "bg-emerald-500" : "bg-gray-500"}`}
                  style={{ width: `${Math.min(data.bridge_phase.pct_to_cutoff, 100)}%` }}
                />
              </div>
            </div>
          )}

          {next_unpaid_milestone && data.bridge_phase?.active && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between" data-testid="vp-kpi-next-payout-banner">
              <div className="text-sm text-emerald-900">
                <strong>Milestone unlocked!</strong> {next_unpaid_milestone.label} → {fmtUSD(next_unpaid_milestone.bonus_usd)} bonus owed.
              </div>
              <Button
                size="sm"
                onClick={() => onMarkMilestonePaid(next_unpaid_milestone.subs_threshold)}
                disabled={markingPaid === next_unpaid_milestone.subs_threshold}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid={`vp-kpi-mark-paid-${next_unpaid_milestone.subs_threshold}`}
              >
                {markingPaid === next_unpaid_milestone.subs_threshold ? <Loader2 className="h-3 w-3 animate-spin" /> : <DollarSign className="h-3 w-3 mr-1" />}
                Mark Paid
              </Button>
            </div>
          )}

          {subscriber_milestones.map((m) => (
            <div
              key={m.subs_threshold}
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                m.paid_out
                  ? "bg-gray-50 border-gray-200 opacity-70"
                  : m.expired
                    ? "bg-gray-50 border-gray-300 opacity-50"
                    : m.achieved
                      ? "bg-amber-50 border-amber-200"
                      : "bg-white border-gray-200"
              }`}
              data-testid={`vp-kpi-milestone-${m.subs_threshold}`}
            >
              <div className="flex-shrink-0 mt-0.5">
                {m.paid_out ? (
                  <CheckCircle2 className="h-5 w-5 text-gray-400" />
                ) : m.expired ? (
                  <Circle className="h-5 w-5 text-gray-300" />
                ) : m.achieved ? (
                  <Trophy className="h-5 w-5 text-amber-500" />
                ) : (
                  <Circle className="h-5 w-5 text-gray-300" />
                )}
              </div>
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="font-medium text-sm text-gray-900 flex items-center gap-2">
                  {m.label}
                  {m.expired && <Badge variant="outline" className="text-[9px] border-gray-400 text-gray-500">EXPIRED</Badge>}
                </div>
                {m.paid_out ? (
                  <div className="text-xs text-gray-500">Paid on {new Date(m.paid_out_at).toLocaleDateString()}</div>
                ) : m.expired ? (
                  <div className="text-xs text-gray-500">Bridge phase ended before this milestone was achieved. No bonus payable.</div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 text-[11px]">
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className={m.subs_condition_met ? "text-emerald-700 font-medium" : "text-gray-500"}>
                            {m.subs_condition_met ? "✓" : "○"} Subs: {m.subs_progress_pct}%
                          </span>
                          <span className="text-gray-400">≥{m.subs_threshold}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1 overflow-hidden">
                          <div className={`h-1 ${m.subs_condition_met ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${m.subs_progress_pct}%` }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className={m.cash_condition_met ? "text-emerald-700 font-medium" : "text-gray-500"}>
                            {m.cash_condition_met ? "✓" : "○"} Cash: {m.cash_progress_pct}%
                          </span>
                          <span className="text-gray-400">≥{fmtUSD(m.cash_collected_floor)}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1 overflow-hidden">
                          <div className={`h-1 ${m.cash_condition_met ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${m.cash_progress_pct}%` }} />
                        </div>
                      </div>
                    </div>
                    <div className="text-[10px] text-gray-400">Both conditions required; bridge phase active</div>
                  </>
                )}
              </div>
              <div className="text-right whitespace-nowrap flex-shrink-0">
                <div className={`font-bold text-sm ${m.expired ? "text-gray-400 line-through" : "text-gray-900"}`}>{fmtUSD(m.bonus_usd)}</div>
                <div className="text-[10px] text-gray-400">{m.subs_threshold} subs</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <p className="text-[10px] text-gray-400 text-center px-4 leading-snug">
        VP earns 1% of quarterly net profit for each KPI hit (max 5%). VP also earns commissions on each new sub: $2.00 per MRR sub, $15.00 per ARR sub (capped at $10K/mo). Sales team earns $1.75 per MRR sub and $15.00 per ARR sub.
        Subscriber milestone bridge bonuses are <strong>active only while company MRR ≤ $5,000</strong> (each milestone requires BOTH the subscriber count threshold AND the cash-collected floor). Above $5,000 MRR, only the quarterly KPI profit-share remains active.
        All numbers refresh in real-time from the production database.
      </p>
    </div>
  );
};

export default AdminVPKpiTab;
